import classNames from 'classnames/bind';
import { BrandChip, IconButton } from '@components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { GetPageWrapperDataQuery } from '@graphqlTypes';
import { useViewSize } from '@/providers/ViewSizeContext';
import ArrowRight from "@assets/svg/arrow-right.svg";

import styles from './Filters.module.scss';
import { scaleVal } from '@/core/utils';
import { usePageWrapperData } from '@/providers/PageWrapperContext';
const cx = classNames.bind(styles);

type EventListingFiltersProps = {
  filterButtons: {
    slug?: string;
    name?: string;
  }[];
  termCounts?: TermCount[];
  typeFilters: string[];
  handleFilterClick: (slug: string) => void;
};

export default function EventListingFilters({ filterButtons, typeFilters, handleFilterClick, termCounts }: EventListingFiltersProps) {
  const { viewWidth } = useViewSize();
  const { labels: { ariaLabels } = {} } = usePageWrapperData();
  const root = useRef<HTMLDivElement>(null);
  const filterWrapper = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [atEnd, setAtEnd] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const filterCounts = useMemo(() => {
    const out: Record<string, number> = {}
    if (!termCounts) return out;
    termCounts.forEach((term) => {
      if (term.termSlug) out[term.termSlug] = term.count || 0;
    });
    return out;
  }, [termCounts]);

  const handleNextClick = () => {
    if (filterWrapper.current) {
      const children = filterWrapper.current.children;
      const { scrollLeft, clientWidth } = filterWrapper.current;
      for (let i = 0; i < children.length; i++) {
        const child = children[i] as HTMLElement;
        const childRight = child.offsetLeft + child.offsetWidth;
        if (childRight > scrollLeft + clientWidth) {
          filterWrapper.current.scrollTo({
            left: child.offsetLeft - scaleVal(80),
            behavior: 'smooth'
          });
          break;
        }
      }
    }
  }

  const handlePrevClick = () => {
    if (filterWrapper.current) {
      const children = filterWrapper.current.children;
      const { scrollLeft, clientWidth } = filterWrapper.current;
      for (let i = children.length - 1; i >= 0; i--) {
        const child = children[i] as HTMLElement;
        const childLeft = child.offsetLeft;
        if (childLeft < scrollLeft) {
          filterWrapper.current.scrollTo({
            left: childLeft - clientWidth + child.offsetWidth + scaleVal(80),
            behavior: 'smooth'
          });
          break;
        }
      }
    }
  }

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollLeft, scrollWidth, clientWidth } = e.currentTarget;
    setAtEnd(scrollLeft + clientWidth >= scrollWidth - 5);
    setIsScrolled(scrollLeft > 0);
  }

  const checkOverflow = () => {
    if (filterWrapper.current) {
      const { scrollWidth, clientWidth } = filterWrapper.current;
      setHasOverflow(scrollWidth > clientWidth);
    }
  }

  useEffect(() => {
    checkOverflow();
  }, [viewWidth]);
  return (
    <div className={cx("filters", { overflow: hasOverflow, scrolled: isScrolled, "scroll-end": atEnd })} ref={root}>
      <div className={cx(["filters__button", "filters__button--prev"])}>
        <IconButton
          onClick={handlePrevClick}
          disabled={!isScrolled}
          size="small"
          ariaLabel={ariaLabels?.filterScroll?.left}
          icon={ArrowRight}
        />
      </div>
      <div
        className={cx("filters__wrapper")}
        ref={filterWrapper}
        onScroll={handleScroll}
      >
        {filterButtons.map((type) => (
          <BrandChip
            className={cx("filter-chip")}
            active={typeFilters.includes(type.slug)}
            label={type.name}
            value={type.slug}
            count={filterCounts[type.slug]}
            onClick={handleFilterClick}
            key={type.slug}
          />
        ))}
      </div>
      <div className={cx(["filters__button", "filters__button--next"])}>
        <IconButton
          disabled={(hasOverflow && !atEnd) ? false : true}
          size="small"
          ariaLabel={ariaLabels?.filterScroll?.right}
          onClick={handleNextClick}
          icon={ArrowRight}
        />
      </div>
    </div>
  )
}